// 2026-09-23 - FormuLearning - MOBO 진척도 차트 — 라운드별 HV 달성률 · 개선 폭 (이슈 #67 S4-8, 잠정안)
// 파일 위치: frontend/src/features/step4/HvProgressChart.tsx
// 목적: MOBO 원본의 «라운드별 누적 HV 추이»(`M915-12` 하이퍼볼륨 증가량 그래프)를 그린다. 원천은 `GET …/analysis` 의
//       `progress`(optimizer `analyze_hv` → 백엔드가 `threshold_pp` 를 덧붙임, 인터페이스 v1.16 §2.6).
//       막대 = 라운드별 달성률 상승 폭(%p) · 선 = 달성률(%, 지금까지 최고 HV = 100) · 점선 = 수렴 안내 기준(%p).
//       통합표 아래 용액별 f2 추이(`BatchF2Chart`, 옛 Convergence 자리)는 **그대로 두고 병존**한다(사용자 260923).
//       축 정의는 `Q-260917-03`(고객 검토) 결과를 받기 전까지 **잠정** 이다 — 화면에 그 표기를 단다.
//       전체 누적 관측이라 라운드 선택과 무관하다(BR-OPT-17).

import { useEffect, useRef } from 'react'
import type { HvProgress, Step4AnalysisView } from '../../api/step4'
import type { RoundListItem } from '../../api/round'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'
import { RecalcBadge } from './FeatureImportance'

const PLOT_OPTS = { displayModeBar: false, responsive: true }

/** x축 라벨 — 라운드 목록에서 찾으면 `Initial` · `Opt.#n`, 없으면 `R{n}`. */
function roundLabel(no: number, rounds: RoundListItem[]): string {
  const r = rounds.find((x) => x.round_no === no)
  if (!r) return `R${no}`
  return r.round_type === 'initial' ? 'Initial' : `Opt.#${r.round_no - 1}`
}

/** 수렴 안내 — 원본은 콘솔 문구, 여기서는 한 줄 상태(BR-OPT-17 · `Q-260917-13` 기준값은 국민대 확인 대기). */
function statusText(p: HvProgress): string {
  if (p.rounds.length < 2) return '라운드가 2개 이상이면 개선 폭과 수렴 여부를 판정합니다'
  const last = p.rounds[p.rounds.length - 1].gain_pp
  const th = p.threshold_pp
  const g = last == null ? '' : `마지막 상승 ${last.toFixed(1)}%p`
  if (p.converged) return `${g} — 기준(${th ?? '?'}%p) 미만 · 수렴에 가까워졌습니다`
  return `${g} — 기준(${th ?? '?'}%p) 이상 · 아직 개선 중입니다`
}

export default function HvProgressChart({ view, rounds }: { view: Step4AnalysisView; rounds: RoundListItem[] }) {
  const theme = useUiStore((s) => s.theme)
  const ref = useRef<HTMLDivElement>(null)
  const p = view.analysis?.progress
  const pts = p?.rounds ?? []

  useEffect(() => {
    if (!ref.current || !p || pts.length === 0) return
    let cancelled = false
    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled || !ref.current) return
      const t = chartTokens()
      const labels = pts.map((r) => roundLabel(r.round_no, rounds))
      const traces: Record<string, unknown>[] = [
        {
          x: labels, y: pts.map((r) => r.gain_pp), name: '개선 폭 (%p)', yaxis: 'y2',
          type: 'bar', marker: { color: t.teal, opacity: 0.45 },
          customdata: pts.map((r) => r.n),
          hovertemplate: '%{x}<br>개선 %{y:+.1f}%p<br>누적 관측 %{customdata}건<extra></extra>',
        },
        {
          x: labels, y: pts.map((r) => r.achievement_pct), name: '달성률 (%)',
          type: 'scatter', mode: 'lines+markers',
          line: { color: t.purple, width: 2 }, marker: { color: t.purple, size: 7 },
          hovertemplate: '%{x}<br>달성률 %{y:.1f}%<extra></extra>',
        },
      ]
      const shapes: Record<string, unknown>[] = []
      const annotations: Record<string, unknown>[] = []
      if (p.threshold_pp != null) {
        shapes.push({ type: 'line', xref: 'paper', yref: 'y2', x0: 0, x1: 1, y0: p.threshold_pp, y1: p.threshold_pp,
                      line: { color: t.amber, width: 1, dash: 'dash' }, opacity: 0.7 })
        annotations.push({ xref: 'paper', yref: 'y2', x: 1, y: p.threshold_pp, xanchor: 'right', yanchor: 'bottom',
                           text: `수렴 기준 ${p.threshold_pp}%p`, showarrow: false, font: { size: 9, color: t.amber } })
      }
      const maxGain = Math.max(p.threshold_pp ?? 0, ...pts.map((r) => r.gain_pp ?? 0))
      Plotly.newPlot(ref.current, traces, {
        paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: t.plotBg,
        font: { color: t.font, size: 10 },
        margin: { t: 26, r: 44, b: 36, l: 44 },
        hovermode: 'x unified',
        xaxis: { type: 'category', gridcolor: t.grid, fixedrange: true },
        yaxis: { title: { text: '달성률 (%)' }, range: [0, 105], gridcolor: t.grid, fixedrange: true },
        yaxis2: { title: { text: '개선 폭 (%p)' }, overlaying: 'y', side: 'right', showgrid: false,
                  range: [0, Math.max(maxGain * 1.15, 1)], fixedrange: true },
        shapes, annotations,
        legend: { orientation: 'h', x: 0, y: 1.02, yanchor: 'bottom', font: { size: 9 } },
      }, PLOT_OPTS)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(p), JSON.stringify(rounds.map((r) => [r.round_no, r.round_type])), theme])

  if (!p || pts.length === 0) return null
  return (
    <section className="mb-3" data-testid="hv-progress">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Optimization Progress</h4>
        <span className="text-[10px] text-text-muted">— Hypervolume 달성률 · 라운드별 개선 폭</span>
        <span className="rounded border px-1.5 py-0.5 text-[10px]" data-testid="hv-provisional"
              title="축 정의는 고객 검토(Q-260917-03) 결과에 따라 바뀔 수 있습니다"
              style={{ borderColor: 'var(--border-subtle)', color: 'var(--color-text-secondary)' }}>
          잠정
        </span>
        {view.recalculating && <RecalcBadge />}
      </div>
      <div ref={ref} style={{ width: '100%', height: 220 }} />
      <p className="mt-1 text-[10px] text-text-secondary" data-testid="hv-status"
         style={p.converged ? { color: 'var(--color-accent-teal)' } : undefined}>
        {statusText(p)}
      </p>
    </section>
  )
}
